import React, { createContext, ReactNode, useState, useEffect, useCallback } from "react";
import { driver } from "driver.js";
import { getSubRoot } from "@/utils/utils";
import { getAllHelps, waitForElement } from '@/utils/tourUtils';
import 'driver.js/dist/driver.css';

import router from 'routes';
import { tourStepDict, TourSteps } from "@/constants/tourSteps";
import axios from "axios";
import axiosIB from '../utils/axios'

export interface HelpTourItems {
    description: string;
    helpTours: string[];
    helpTutorials: string[];
    helpTopics: string[];
}

interface stepData {
    agenticDataType: string;
    agenticDataValue: string;
    isCertain: boolean;
}

interface TourContextType {
    startTour: (steps: any[], startIndex?: number) => void;
    startTourHelp: (option: string) => void;
    executeAgenticTour: (steps: any[], data: stepData[], startIndex: number) => void;
    getAvailableHelps: () => HelpTourItems;
    guide: any;
}

/**
 * NOTE
 * TECNICHE
 * IMPORTANTISSIME
 *
 * - Attenzione a quando crei i toursteps. devono essere tutti in tourSteps.ts
 *
 * - La prima rotta di ogni step è NECESSARIA. Così da capire dove deve andare a finire. Vogliamo rendere tutto dinamico o no?
 *
 * - I cambi di rotta devono OVVIAMENTE ESSERE SEGNATI negli step rilevanti
 *
 * */




export const TourContext = createContext<TourContextType | undefined>(undefined);

interface Props {
    children: ReactNode;
}

export const TourProvider = ({ children }: Props) => {
    const [currentPath, setCurrentPath] = useState(router.state.location.pathname);
    const [pendingSteps, setPendingSteps] = useState<any[]>([]);
    const [pendingIndex, setPendingIndex] = useState<number | null>(null);
    const [guide, setGuide] = useState<any>(null);

    useEffect(() => {
        const getTours = async () => {
            const returnedInfo = await axios.get(import.meta.env?.VITE_TOUR_URL);
            return returnedInfo.data;
        };
        getTours().then(r => {
            if (r) setGuide(r);
        }).catch(async error => {
            try {
                const retInfo = await axiosIB.get('/tour');
                setGuide(retInfo.data)
            }
            catch(error) {
                console.error(error);
                console.warn('It wasn\'t possible to retrieve tours from backup')
            }
        });
    }, [router.state.location.pathname]);

    useEffect(() => {
        const unsubscribe = router.subscribe((state) => {
            setCurrentPath(state.location.pathname);
        });
        return () => unsubscribe();
    }, []);

    const startDriverEngine = useCallback((steps: any[], startIndex: number) => {
        if (!steps || steps.length === 0) return;

        const actualPath = router.state.location.pathname;
        const targetStep = steps[startIndex];

        if (targetStep?.route && !actualPath.includes(targetStep.route)) {
            setPendingSteps(steps);
            setPendingIndex(startIndex);
            const basePath = getSubRoot(actualPath);
            router.navigate(`${basePath}/${targetStep.route}`.replace(/\/+/g, '/'));
            return;
        }

        let driverObj: any;

        const handlePrevious = async () => {
            if (!driverObj) return;

            try {
                const state = driverObj.getState();
                const currentIndex = state.activeIndex ?? 0;
                let prevIndex = currentIndex - 1;

                const currentStep = steps[currentIndex];

                // Guardia di sicurezza
                const disabledBtns = currentStep?.popover?.disableButtons || [];
                const showBtns = currentStep?.popover?.showButtons;
                if (disabledBtns.includes('previous') || (showBtns && !showBtns.includes('previous'))) {
                    return;
                }


                while (prevIndex >= 0) {
                    const prevStep = steps[prevIndex];
                    const currentActualPath = router.state.location.pathname;

                    if (prevStep?.route && !currentActualPath.includes(prevStep.route)) {
                        driverObj.destroy();
                        setPendingSteps(steps);
                        setPendingIndex(prevIndex);
                        const basePath = getSubRoot(currentActualPath);
                        router.navigate(`${basePath}/${prevStep.route}`.replace(/\/+/g, '/'));
                        return;
                    }

                    const el = prevStep?.element ? document.querySelector(prevStep.element) : null;
                    if (el || prevStep?.skipMissingElement !== true) {
                        driverObj.moveTo(prevIndex);
                        return;
                    }

                    prevIndex--;
                }
            } catch (e) {
                driverObj.destroy();
            }
        };

        const handleNext = async () => {
            if (!driverObj) return;

            try {
                const state = driverObj.getState();
                const currentIndex = state.activeIndex ?? 0;
                const currentStep = steps[currentIndex];
                const element = driverObj.getActiveElement();

                if (currentStep?.beforeNext) {
                    try {
                        const result = await currentStep.beforeNext(element, currentStep);
                        if (result === false) return;
                    } catch (error) {
                        console.error("Errore validazione step:", error);
                        driverObj.destroy();
                        return;
                    }
                }

                const nextIndex = currentIndex + 1;
                const nextStep = steps[nextIndex];
                const currentActualPath = router.state.location.pathname;

                if (nextStep?.route && !currentActualPath.includes(nextStep.route)) {
                    driverObj.destroy();
                    setPendingSteps(steps);
                    setPendingIndex(nextIndex);
                    const basePath = getSubRoot(currentActualPath);
                    router.navigate(`${basePath}/${nextStep.route}`.replace(/\/+/g, '/'));
                } else {
                    setTimeout(() => {
                        driverObj.moveNext();
                    }, 100);
                }
            } catch (e) {
                driverObj.destroy();
            }
        };

        const stepsWithHelpers = steps.map((step) => {
            const originalOnHighlightStarted = step.onHighlightStarted;
            return {
                ...step,
                onHighlightStarted: async (element: Element, stepInfo: any, options: any) => {
                    if (typeof originalOnHighlightStarted === 'function') {
                        await originalOnHighlightStarted(element, stepInfo, {
                            ...options,
                            handleNext
                        });
                    }
                }
            };
        });

        let preventLeftArrowListener: ((e: KeyboardEvent) => void) | null = null;

        const cleanupLeftArrowBlock = () => {
            if (preventLeftArrowListener) {
                document.removeEventListener('keydown', preventLeftArrowListener, true);
                preventLeftArrowListener = null;
            }
        };

        driverObj = driver({
            popoverClass: 'inBrief-popover',
            showButtons: ['close', 'next', 'previous'],
            duration: 150,
            steps: stepsWithHelpers,

            onHighlightStarted: (element, step, options) => {
                cleanupLeftArrowBlock();
                const currentIndex = options.state.activeIndex ?? 0;
                const currentStep = steps[currentIndex];

                // Controlla se il previous è disabilitato o nascosto nello step corrente
                const disabledBtns = currentStep?.popover?.disableButtons || [];
                const showBtns = currentStep?.popover?.showButtons;
                const isPrevDisabled = disabledBtns.includes('previous') || (showBtns && !showBtns.includes('previous'));

                if (isPrevDisabled) {
                    preventLeftArrowListener = (e: KeyboardEvent) => {
                        if (e.key === 'ArrowLeft') {
                            e.preventDefault();
                            e.stopImmediatePropagation(); // Ferma l'evento prima che arrivi al listener globale di driver.js
                        }
                    };
                    document.addEventListener('keydown', preventLeftArrowListener, true);
                }

                if (typeof currentStep?.onHighlightStarted === 'function') {
                    currentStep.onHighlightStarted(element, step, { ...options, handleNext });
                }
            },

            onDeselected: (element, step, { state }) => {
                cleanupLeftArrowBlock();

                if (element && (element as any)._tourInterval) {
                    clearInterval((element as any)._tourInterval);
                    delete (element as any)._tourInterval;
                }

                const nextBtn = document.querySelector('.driver-popover-next-btn') as HTMLElement | null;
                if (nextBtn) {
                    nextBtn.classList.remove('disabled');
                    nextBtn.removeAttribute('disabled');
                    nextBtn.style.pointerEvents = 'auto';
                    nextBtn.style.opacity = '1';
                }

                const currentIndex = state.activeIndex ?? 0;
                const currentStep = steps[currentIndex];
                if (typeof currentStep?.onDeselected === 'function') {
                    currentStep.onDeselected(element);
                }
            },

            onDestroyStarted: () => {
                cleanupLeftArrowBlock();
                if (driverObj) driverObj.destroy();
            },

            onNextClick: () => handleNext(),
            onPrevClick: () => handlePrevious()
        });

        driverObj.drive(startIndex);
    }, []);

    const startTour = async (steps: any[], startIndex: number = 0) => {
        if (!steps || steps.length === 0) return;

        const currentStep = steps[startIndex];
        const actualPath = router.state.location.pathname;

        // 1. Se siamo su un'altra rotta, navighiamo prima di tentare qualunque check
        if (currentStep?.route && !actualPath.includes(currentStep.route)) {
            setPendingSteps(steps);
            setPendingIndex(startIndex);
            const basePath = getSubRoot(actualPath);
            router.navigate(`${basePath}/${currentStep.route}`.replace(/\/+/g, '/'));
            return;
        }

        let visualSteps = [...steps];

        // 2. Eseguiamo il locationCheck SOLO ORA che siamo sulla rotta corretta
        if (typeof currentStep?.locationCheck === 'function') {
            try {
                await currentStep.locationCheck();
            } catch (error) {
                console.error("LocationCheck fallito, interrompo per evitare vista errata:", error);
                return;
            }

            if (!currentStep.popover && !currentStep.element) {
                visualSteps.splice(startIndex, 1);
            }
        }

        if (visualSteps.length === 0) return;

        // 3. Attendiamo che il primo elemento reale sia presente a schermo
        const nextTarget = visualSteps[startIndex];
        if (nextTarget?.element) {
            try {
                await waitForElement(nextTarget.element, nextTarget.waitForElement || 10000);
            } catch (e) {
                console.warn("Elemento non comparso in tempo:", nextTarget.element);
            }
        }

        startDriverEngine(visualSteps, startIndex);
    };

    // Unico listener per la ripresa del tour dopo il cambio rotta
    useEffect(() => {
        if (pendingIndex !== null && pendingSteps.length > 0) {
            const indexToResume = pendingIndex;
            const stepsToResume = pendingSteps;
            const targetStep = stepsToResume[indexToResume];
            const targetRoute = targetStep?.route;

            if (targetRoute && !currentPath.includes(targetRoute)) {
                return;
            }

            setPendingIndex(null);
            setPendingSteps([]);

            setTimeout(() => {
                startTour(stepsToResume, indexToResume);
            }, 200);
        }
    }, [currentPath, pendingIndex, pendingSteps]);

    const executeAgenticTour = useCallback((inputSteps: any[], data: stepData[], startIndex: number) => {
        let steps: TourSteps[] = [];

        for (let inputStep of inputSteps) {
            if (typeof inputStep === 'string' && Object.keys(tourStepDict).includes(inputStep)) {
                steps.push(tourStepDict[inputStep]);
            } else if (typeof inputStep === 'object') {
                steps.push(inputStep);
            }
        }

        const agenticSteps = steps.map((x, index) => ({
            ...x,
            agenticData: data[index],
            isAgentic: true
        }));

        startTour(agenticSteps, startIndex);
    }, []);

    const executeTour = useCallback((steps: any[], startIndex: number) => {
        const tourSteps = steps.map((x) => ({ ...x, isAgentic: false }));
        startDriverEngine(tourSteps, startIndex);
    }, [startDriverEngine]);

    const startTourHelp = (option: string) => {
        let steps: TourSteps[] = [];
        if (guide?.tourAssociations && Object.keys(guide.tourAssociations).includes(option)) {
            for (const val of guide.tourAssociations[option]) {
                steps.push(tourStepDict[val]);
            }
        }
        startTour(steps, 0);
    };

    const getAvailableHelps = (): HelpTourItems => {
        try {
            if (guide === null) return {description: '', helpTours: null, helpTutorials: null, helpTopics: null};
            const allHelps = getAllHelps(guide, currentPath);

            return {
                description: allHelps.description,
                helpTours: allHelps.howTos,
                helpTutorials: allHelps.tutorials,
                helpTopics: allHelps.relatedTutorials,
            };
        }
        catch (error) {
            console.error(error)
        }
    };

    return (
        <TourContext.Provider value={{ startTour, startTourHelp, executeAgenticTour, guide, getAvailableHelps }}>
            {children}
        </TourContext.Provider>
    );
};